import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Card,
  IconButton,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";
import { arSA } from "@mui/material/locale";
import { useGetFinancialPeriodsQuery } from "../../../redux/Slices/financial-periodsApi";

const CreateJournalEntries = ({ onClose, onSuccess }) => {
  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const { data: periods, isLoading: periodsLoading } = useGetFinancialPeriodsQuery();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    date: new Date(),
    description: "",
    total_debit: 0,
    total_credit: 0,
    fiscal_period_id: "4",
    details: [
      {
        account_id: "",
        debit_amount: 0,
        credit_amount: 0,
        description: "",
        reference: "",
      },
      {
        account_id: "",
        debit_amount: 0,
        credit_amount: 0,
        description: "",
        reference: "",
      },
    ],
  });

  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Main form validation
    if (!formData.date) newErrors.date = "تاريخ المعاملة مطلوب";
    if (!formData.description.trim()) newErrors.description = "وصف المعاملة مطلوب";

    if (formData.total_debit !== formData.total_credit) {
      newErrors.total = "إجمالي المدين يجب يساوي إجمالي الدائن";
    }

    // Details validation
    formData.details.forEach((detail, index) => {
      if (!detail.account_id) {
        newErrors[`detail_${index}_account`] = "الحساب مطلوب";
      }
      if (!detail.description.trim()) {
        newErrors[`detail_${index}_description`] = "وصف البند مطلوب";
      }
      if (detail.debit_amount === 0 && detail.credit_amount === 0) {
        newErrors[`detail_${index}_amount`] = "يجب إدخال مبلغ مدين أو دائن";
      }
      if (detail.debit_amount > 0 && detail.credit_amount > 0) {
        newErrors[`detail_${index}_amount`] = "لا يمكن أن يكون المبلغ مدين ودائن في نفس الوقت";
      }
    });

    // At least 2 details required
    if (formData.details.length < 2) {
      newErrors.details = "يجب إضافة عنصرين على الأقل";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);
  useEffect(() => {
    if (formSubmitted) validateForm();
  }, [formSubmitted, validateForm]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const removeDetail = (index) => {
    if (formData.details.length > 2) {
      setFormData((prev) => ({
        ...prev,
        details: prev.details.filter((_, i) => i !== index),
      }));
    }
  };
  // create or update journal entry
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    try {
      const submitData = {
        ...formData,
        /* date: formData.date.toString().split("T")[0], */
        date: formData.date.toISOString().split("T")[0],
        total_debit: parseFloat(formData.total_debit),
        total_credit: parseFloat(formData.total_credit),
        details: formData.details.map((detail) => ({
          ...detail,
          account_id: parseInt(detail.account_id),
          debit_amount: parseFloat(detail.debit_amount),
          credit_amount: parseFloat(detail.credit_amount),
        })),
      };

      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
      const token = localStorage.getItem("user-token");
      const endpoint = formData.id
        ? `https://back.al-balad.sa/albalad/v1.0/accounting/accounts/update/${formData.id}`
        : "https://back.al-balad.sa/albalad/v1.0/accounting/journal-entries/store";

      const response = await axios.post(endpoint, submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      // Close loading indicator
      Swal.close();

      if (response.success) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            container.style.zIndex = "99999";
          },
        });
        onSuccess();
        onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      console.error("Error Details:", error);
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء  الحفظ";

      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };
  const handleMainFieldChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Recalculate totals when amounts change
    if (field === "total_debit" || field === "total_credit") {
      const otherField = field === "total_debit" ? "total_credit" : "total_debit";
      setFormData((prev) => ({
        ...prev,
        [otherField]: value,
      }));
    }
  };
  const handleDetailChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const newDetails = [...prev.details];
      newDetails[index] = {
        ...newDetails[index],
        [field]: value,
      };

      // Auto-calculate totals
      const totalDebit = newDetails.reduce(
        (sum, detail) => sum + parseFloat(detail.debit_amount || 0),
        0
      );
      const totalCredit = newDetails.reduce(
        (sum, detail) => sum + parseFloat(detail.credit_amount || 0),
        0
      );

      return {
        ...prev,
        details: newDetails,
        total_debit: totalDebit,
        total_credit: totalCredit,
      };
    });
  };

  const addDetail = () => {
    setFormData((prev) => ({
      ...prev,
      details: [
        ...prev.details,
        {
          account_id: "",
          debit_amount: 0,
          credit_amount: 0,
          description: "",
          reference: "",
        },
      ],
    }));
  };
  const handleCancel = () => {
    setFormData({});
    setErrors({});
    onClose();
  };
  return (
    <Box
      sx={{
        p: 3,
        width: "100%",
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        borderRadius: 2,
        boxShadow: 3,
        color: darkMode ? "white !important" : "inherit",
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          قيد تسوية نقدية
        </Typography>
      </Box>

      {/* Main Form */}
      <LocalizationProvider locale={arSA} dateAdapter={AdapterDateFns}>
        <Box component="form" onSubmit={handleSubmit} sx={{ margin: "auto", p: 3 }}>
          {/* Main Form Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DatePicker
                label="تاريخ المعاملة *"
                value={formData.date}
                sx={{ color: "text.main"  }}
                size="medium"
                onChange={(newValue) => setFormData((prev) => ({ ...prev, date: newValue }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.date,
                    helperText: errors.date,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: "2px !important",
                        
                       
                        color: "text.main",
                        fontSize: ".85rem",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "5px 10px !important",
                        height: "auto",
                        color: "text.main",
                        WebkitTextFillColor: "text.main",
                        fontSize: ".85rem",
                      },
                      "& .MuiInputBase-input": {
                        color: "text.main",
                        WebkitTextFillColor: "text.main",
                        fontSize: ".85rem",
                      },
                      "& .MuiPickersSectionList-root": {
                        color: "text.main",
                        fontSize: ".85rem",
                        padding: "12px 10px !important",
                        
                      },
                      "& .MuiPickersSectionList-section": {
                        color: "text.main",
                        fontSize: ".85rem",
                      },
                      "& .MuiPickersSectionList-sectionContent": {
                        color: "text.main",
                        fontSize: ".85rem",
                      },
                      "& .MuiDateSectionInput-input": {
                        color: "text.main",
                        fontSize: ".85rem",
                      },
                      "& input": {
                        color: "text.main",
                        WebkitTextFillColor: "text.main",
                        fontSize: ".85rem",
                      },
                      "& .MuiInputLabel-root": {
                        transform: "translate(14px, 6px) scale(1)",
                        color: "text.main",
                        fontSize: ".85rem",
                        "&.MuiInputLabel-shrink": {
                          transform: "translate(14px, -8px) scale(0.75)",
                        },
                      },
                     "& .MuiPickersOutlinedInput-notchedOutline": {
                      borderColor: "text.border",
                    },
                      
                    },
                  },
                  openPickerButton: {
                    sx: {
                      color: "text.main",
                      padding: "4px",
                    },
                  },
                  openPickerIcon: {
                    sx: {
                      color: "text.main",
                      fill: "text.main",
                      fontSize: "1.1rem",
                    },
                  },
                }}
              />
              {/* <TextField
                fullWidth
                label="تاريخ المعاملة *"
                type="date"
                value={formData.date}
                onChange={handleMainFieldChange("date")}
                error={!!errors.date}
                helperText={errors.date}
              /> */}
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="type"> اختر الفترة </InputLabel>
                <Select
                  label="اختر الفترة "
                  /* error={!!errors.type} */
                  name="type"
                  value={formData.fiscal_period_id}
                  onChange={handleChange}
                  sx={{ height: 43 }}
                >
                  {periods?.data?.map((period) => (
                    <MenuItem key={period.id} value={period.id}>
                      {period.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="إجمالي المدين *"
                type="number"
                value={formData.total_debit}
                onChange={handleMainFieldChange("total_debit")}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                error={!!errors.total}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="إجمالي الدائن *"
                type="number"
                value={formData.total_credit}
                onChange={handleMainFieldChange("total_credit")}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                error={!!errors.total}
                helperText={errors.total}
              />
            </Grid>
          </Grid>
          <Grid item sx={12}>
            <TextField
              fullWidth
              label="وصف المعاملة *"
              value={formData.description}
              onChange={handleMainFieldChange("description")}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid>
          
          <Typography variant="h5" my={2}>
            تفاصيل القيد
          </Typography>

          {errors.details && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.details}
            </Alert>
          )}

          {/* Details Section */}
          {formData?.details?.map((detail, index) => (
            <Card
              key={index}
              sx={{ mb: 2, border: "1px solid", borderColor: "divider", padding: 2 }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth error={!!errors[`detail_${index}_account`]}>
                    <TextField
                      fullWidth
                      label="حساب"
                      type="number"
                      value={detail.account_id}
                      onChange={handleDetailChange(index, "account_id")}
                      error={!!errors[`detail_${index}_account`]}
                      helperText={errors[`detail_${index}_account`]}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="المدين"
                    type="number"
                    value={detail.debit_amount}
                    onChange={handleDetailChange(index, "debit_amount")}
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    error={!!errors[`detail_${index}_amount`]}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="الدائن"
                    type="number"
                    value={detail.credit_amount}
                    onChange={handleDetailChange(index, "credit_amount")}
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    error={!!errors[`detail_${index}_amount`]}
                    helperText={errors[`detail_${index}_amount`]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="الوصف *"
                    value={detail.description}
                    onChange={handleDetailChange(index, "description")}
                    error={!!errors[`detail_${index}_description`]}
                    helperText={errors[`detail_${index}_description`]}
                  />
                </Grid>

                <Grid item xs={12} md={1}>
                  <TextField
                    fullWidth
                    label="المرجع"
                    value={detail.reference}
                    onChange={handleDetailChange(index, "reference")}
                  />
                </Grid>

                <Grid item xs={12} md={1}>
                  <IconButton
                    onClick={() => removeDetail(index)}
                    disabled={formData.details.length <= 2}
                    color="text.main"
                     sx={{ color: "text.main" ,"& .MuiButtonBase-root-MuiIconButton-root.Mui-disabled": { color: "text.main" } }}
                  >
                    <Delete color="text.main" />
                  </IconButton>
                </Grid>
              </Grid>
            </Card>
          ))}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: "#b88c36",
                  color: "#b88c36",
                }}
                startIcon={<Add />}
                onClick={addDetail}
                size="medium"
              >
                إضافة بند جديد
              </Button>

              <Button
                type="submit"
                size="medium"
                sx={{
                  minWidth: 120,
                  bgcolor: "#c29d54",
                  color: "#fff",
                  "& .MuiButtonBase-root-MuiButton-root:hover": {
                    bgcolor: "#b88c36 !important",
                    color: "#fff !important",
                  },
                  "&:hover": { bgcolor: "#b88c36", color: "#fff" },
                  "&:focus": { bgcolor: "#b88c36", color: "#fff !important" },
                }}
              >
                حفظ القيد
              </Button>
            </Box>
            <Box>
              <Button
                type="button"
                size="medium"
                sx={{
                  color: "#b88c36",
                  width: 80,
                  mr: "auto",
                  "&:hover": { bgcolor: "#transparent", color: "#b88c36" },
                }}
                onClick={handleCancel}
              >
                إلغاء
              </Button>
            </Box>
          </Box>
        </Box>
      </LocalizationProvider>
    </Box>

    /* </Dialog> */
  );
};

export default CreateJournalEntries;
